import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Heart, Smile, Meh, Frown, Send, Loader2, CheckCircle2, AlertCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { fetchPublicTastingInfo, submitPublicFeedback } from "../lib/api";

// Public, no-auth feedback page. Loaded by a buyer/manager scanning the
// QR code displayed on the picture frame at the bus exit.
// Mobile-first — most submissions will be on a phone.
//
// NOTE on branding: this page is consumer-facing, so it uses the
// public-facing brand ("The Original Jack Herer / Jack Enterprises"),
// NOT the internal CRM brand (JackTrack). End users should never see
// internal tooling names.

const RATINGS = [
  { value: "great", label: "Great",     Icon: Heart, color: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", active: "bg-emerald-500/25 border-emerald-400" },
  { value: "good",  label: "Good",      Icon: Smile, color: "text-sky-300     border-sky-500/40     bg-sky-500/10",     active: "bg-sky-500/25     border-sky-400" },
  { value: "okay",  label: "Okay",      Icon: Meh,   color: "text-amber-300   border-amber-500/40   bg-amber-500/10",   active: "bg-amber-500/25   border-amber-400" },
  { value: "bad",   label: "Not great", Icon: Frown, color: "text-rose-300    border-rose-500/40    bg-rose-500/10",    active: "bg-rose-500/25    border-rose-400" },
];

export default function FeedbackPage() {
  const { tastingId } = useParams();
  const [info, setInfo] = useState(null);     // null = loading, "404" = not found
  const [rating, setRating] = useState(null);
  const [shoutouts, setShoutouts] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Override the document title so the browser tab + share previews
  // reflect the consumer brand, not "JackTrack · CA Dispensary
  // Research".
  useEffect(() => {
    const prev = document.title;
    document.title = "Feedback · The Original Jack Herer";
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    fetchPublicTastingInfo(tastingId)
      .then(setInfo)
      .catch(() => setInfo("404"));
  }, [tastingId]);

  const submit = async () => {
    if (submitting) return;
    setError(null);
    if (!rating && !shoutouts.trim() && !feedback.trim()) {
      setError("Tap a rating or leave a note — even one word helps.");
      return;
    }
    try {
      setSubmitting(true);
      await submitPublicFeedback(tastingId, {
        rating,
        shoutouts: shoutouts.trim(),
        feedback: feedback.trim(),
      });
      setSubmitted(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't submit. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  if (info === null) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (info === "404" || !info.is_open) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6">
        <div className="text-center max-w-sm" data-testid="feedback-closed">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">This feedback link isn't active</h1>
          <p className="text-zinc-400 text-sm">
            The event may have been cancelled or the link is mistyped. If
            you'd like to share feedback, message the host directly.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6">
        <div className="text-center max-w-sm" data-testid="feedback-thanks">
          <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold mb-2">Thank you!</h1>
          <p className="text-zinc-400 text-sm leading-relaxed">
            Your feedback helps us bring better events back to{" "}
            <span className="text-zinc-200">{info.dispensary_name}</span>.
          </p>
          <p className="mt-8 text-[11px] uppercase tracking-[0.15em] font-mono text-emerald-300/80">
            The Original Jack Herer
          </p>
          <p className="text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-600 mt-0.5">
            Jack Enterprises
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="max-w-lg mx-auto px-5 py-8 sm:py-12">
        <header className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-emerald-300/80 mb-1">
            The Original Jack Herer · Bus Event
          </div>
          <h1
            className="text-2xl sm:text-3xl font-semibold tracking-tight"
            data-testid="feedback-dispensary-name"
          >
            {info.dispensary_name}
          </h1>
          {info.tasting_date && (
            <div className="text-sm text-zinc-400 mt-1 font-mono">
              {new Date(info.tasting_date + "T00:00:00").toLocaleDateString("en-US", {
                weekday: "long", month: "long", day: "numeric",
              })}
            </div>
          )}
          <p className="mt-5 text-zinc-400 text-sm leading-relaxed">
            Your anonymous feedback helps us create a better experience
            for everyone. Thank you!
          </p>
        </header>

        {/* Rating tap */}
        <section className="mb-7">
          <label className="block text-xs uppercase tracking-wider font-mono text-zinc-500 mb-3">
            How was it?
          </label>
          <div className="grid grid-cols-4 gap-2" data-testid="feedback-ratings">
            {RATINGS.map(({ value, label, Icon, color, active }) => (
              <button
                key={value}
                type="button"
                data-testid={`feedback-rating-${value}`}
                onClick={() => setRating(value)}
                className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 py-4 transition-all ${
                  rating === value ? active : color
                }`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-xs font-medium">{label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Shoutouts */}
        <section className="mb-6">
          <label className="block text-xs uppercase tracking-wider font-mono text-zinc-500 mb-2">
            Anyone stand out?
          </label>
          <Input
            data-testid="feedback-shoutouts"
            value={shoutouts}
            onChange={(e) => setShoutouts(e.target.value)}
            placeholder="A host, driver, rep, anyone…"
            className="bg-zinc-900 border-zinc-800 text-zinc-100"
          />
        </section>

        {/* Feedback */}
        <section className="mb-7">
          <label className="block text-xs uppercase tracking-wider font-mono text-zinc-500 mb-2">
            Anything we could do better?
          </label>
          <Textarea
            data-testid="feedback-text"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={4}
            placeholder="Optional — all feedback helps!"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 resize-none"
          />
        </section>

        {error && (
          <div
            className="mb-4 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2"
            data-testid="feedback-error"
          >
            {error}
          </div>
        )}

        <Button
          data-testid="feedback-submit"
          onClick={submit}
          disabled={submitting}
          className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-emerald-50 text-base font-semibold rounded-xl"
        >
          {submitting ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Send className="w-5 h-5 mr-2" />
          )}
          Send feedback
        </Button>

        <footer className="mt-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.18em] font-mono text-emerald-300/80">
            The Original Jack Herer
          </p>
          <p className="text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-600 mt-0.5">
            Jack Enterprises
          </p>
        </footer>
      </div>
    </div>
  );
}
